import { isValid, parseISO, startOfDay } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	PERMISSION_SETTINGS,
	WORKER_REQUESTS_PERMISSIONS,
} from "../../../../config/permissions";
import { StoreType } from "../../../../utils/types";
import type { PermissionSetting } from "../../../user/user-slice";
import { fetchUserPermissions } from "../../../user/user-slice";

export interface PastTimeOffLookback {
	// Earliest date that can be picked. null means no lower bound.
	earliestDate: Date | null;
	isUnlimited: boolean;
}

// Reads the past-date limit for one location out of permissionSettings. A
// location that isn't listed means no past dates there, and so do settings
// that haven't loaded yet: only today onwards.
export const getPastTimeOffLookback = (
	permissionSettings: PermissionSetting[] | null,
	locationId: number | string | null | undefined,
	now: Date = new Date()
): PastTimeOffLookback => {
	const todayOnly = { earliestDate: startOfDay(now), isUnlimited: false };

	const lookback = permissionSettings?.find(
		(entry) =>
			entry.code === WORKER_REQUESTS_PERMISSIONS.CREATE_PAST_TIME_OFF &&
			entry.setting === PERMISSION_SETTINGS.LOOKBACK_DAYS
	);
	const location = lookback?.locations.find(
		(entry) => Number(entry.locationId) === Number(locationId)
	);
	if (!location) return todayOnly;

	if (location.earliestAllowedDate === null) {
		return { earliestDate: null, isUnlimited: true };
	}

	// The backend already worked the date out in the location's time zone. A
	// date-only string parses to local midnight, which is what the calendar
	// compares against.
	const earliestDate = parseISO(location.earliestAllowedDate);
	if (!isValid(earliestDate)) return todayOnly;

	return { earliestDate: startOfDay(earliestDate), isUnlimited: false };
};

// How far back the current user may mark time off for a worker at the given
// location (GAT-7829). The backend works this out per role and location and
// sends it in GET /Users/Permissions.
const usePastTimeOffLookback = (
	locationId: number | string | null | undefined
): PastTimeOffLookback => {
	const dispatch = useDispatch<any>();
	const permissionSettings = useSelector(
		(state: StoreType) => state.user.userData.permissionSettings
	);
	const permissionsLoadStatus = useSelector(
		(state: StoreType) => state.user.permissionsLoad.status
	);
	const hasRequestedSettings = useRef(false);

	// The login response carries permission codes but not their settings, so
	// they're missing until the first refresh. Ask for them once.
	useEffect(() => {
		if (
			permissionSettings === null &&
			permissionsLoadStatus !== "pending" &&
			!hasRequestedSettings.current
		) {
			hasRequestedSettings.current = true;
			dispatch(fetchUserPermissions());
		}
	}, [dispatch, permissionSettings, permissionsLoadStatus]);

	return useMemo(
		() => getPastTimeOffLookback(permissionSettings, locationId),
		[locationId, permissionSettings]
	);
};

export default usePastTimeOffLookback;
